import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2, Building2 } from "lucide-react";
import api from "../lib/api";
import { TenantLogo, invalidateTenantLogo, useTenantBranding } from "./TenantLogo";

/**
 * Card gestione logo aziendale (co-branding).
 * - Admin: dal Profilo (org propria, senza orgId)
 * - Super admin: dalla vista org (orgId esplicito)
 */
const ACCEPT = ".png,.jpg,.jpeg,.svg";
const MAX_BYTES = 1024 * 1024;

export const BrandingCard = ({ orgId }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const info = useTenantBranding(orgId, refreshKey);
  const q = orgId ? `?org_id=${encodeURIComponent(orgId)}` : "";

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("Il logo supera 1 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/org/branding/logo${q}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      invalidateTenantLogo(orgId);
      setRefreshKey((k) => k + 1);
      toast.success("Logo aziendale aggiornato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel caricamento del logo");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      await api.delete(`/org/branding/logo${q}`);
      invalidateTenantLogo(orgId);
      setRefreshKey((k) => k + 1);
      toast.success("Logo rimosso");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella rimozione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5" data-testid="branding-card">
      <div className="flex items-center gap-2 mb-1">
        <Building2 size={15} className="text-accent" />
        <h3 className="text-sm font-semibold text-text-primary">Logo aziendale</h3>
      </div>
      <p className="text-xs text-text-secondary mb-4">
        Affianca il marchio Wattline in sidebar e nelle riunioni video. PNG, JPG o SVG, max 1 MB.
      </p>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="h-16 min-w-[96px] px-3 rounded-lg border border-border bg-subtle flex items-center justify-center"
             data-testid="branding-preview">
          {info?.has_logo ? (
            <TenantLogo orgId={orgId} maxHeight={44} refreshKey={refreshKey} testId="branding-logo-img" />
          ) : (
            <span className="text-[11px] text-text-muted">Nessun logo</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept={ACCEPT} onChange={onFile} className="hidden"
                 data-testid="branding-file-input" />
          <button type="button" onClick={onPick} disabled={busy}
                  className="wl-btn wl-btn--primary wl-btn--sm" data-testid="branding-upload-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
            {info?.has_logo ? "Sostituisci" : "Carica logo"}
          </button>
          {info?.has_logo && (
            <button type="button" onClick={onRemove} disabled={busy}
                    className="wl-btn wl-btn--ghost wl-btn--sm" style={{ color: "var(--wl-danger-text)" }}
                    data-testid="branding-remove-btn">
              <Trash2 size={13} /> Rimuovi
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
